import { Component, Show } from 'solid-js';
import { ModusWcSliderCustomEvent } from '@trimble-oss/moduswebcomponents';
import { t } from '@trimble/i18n';

const MAX_OPACITY_PERCENT = 100;

export interface TransparencyRowProps {
  readonly id: string;
  readonly labelKey: string;
  readonly sliderTestId: string;
  readonly min: number;
  readonly percent: number;
  readonly isDisabled?: boolean;
  readonly disabledTooltipKey?: string;
  /**
   * Shown to the right of the label when the row is unlinked from a
   * shared upstream value (e.g. main-toolbar global). Click invokes
   * {@link onRelink}, snapping the row back to upstream. Omitted rows
   * never render the indicator.
   */
  readonly isUnlinked?: boolean;
  readonly relinkTooltipKey?: string;
  onSliderChange(ev: ModusWcSliderCustomEvent<InputEvent>): void;
  onRelink?(): void;
}

/**
 * One labelled slider row in a visibility menu. Layout only — opacity
 * state and side-effects are owned by the parent. Optionally renders a
 * "sync to global" affordance when {@link isUnlinked} is true.
 */
export const TransparencyRow: Component<TransparencyRowProps> = (props) => (
  <div
    class={`flex flex-col gap-1 ${props.isDisabled ? 'opacity-50' : ''}`}
    data-testid={`visibility-menu-row-${props.id}`}
    title={
      props.isDisabled && props.disabledTooltipKey
        ? t(props.disabledTooltipKey)
        : undefined
    }
  >
    <div class="flex items-center justify-between gap-2">
      <span class="text-sm text-base-content">{t(props.labelKey)}</span>
      <div class="flex items-center gap-2">
        <div class="flex h-5 w-5 items-center justify-center">
          <Show when={props.isUnlinked && props.onRelink}>
            <button
              type="button"
              class="flex h-full w-full items-center justify-center rounded text-base-content hover:bg-[var(--modus-wc-color-base-100)]"
              data-testid={`visibility-relink-${props.id}`}
              onClick={() => props.onRelink?.()}
              title={t(
                props.relinkTooltipKey ??
                  'mobileMapping.transparency.syncToGlobal'
              )}
              aria-label={t(
                props.relinkTooltipKey ??
                  'mobileMapping.transparency.syncToGlobal'
              )}
            >
              <i class="icon-font tc-icon-sync" />
            </button>
          </Show>
        </div>
        <span class="min-w-[3rem] text-right text-sm text-base-content tabular-nums">
          {props.percent}%
        </span>
      </div>
    </div>
    <div class="trimble-slider slider-horizontal w-full">
      <modus-wc-slider
        attr:data-testid={props.sliderTestId}
        class="w-full h-6 min-h-6"
        min={props.min}
        max={MAX_OPACITY_PERCENT}
        step={1}
        value={props.percent}
        size="sm"
        disabled={props.isDisabled}
        on:inputChange={(ev) => props.onSliderChange(ev)}
      />
    </div>
  </div>
);
